import React, { useState, useRef } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FiMenu } from 'react-icons/fi';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

function Social({ website, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState((website && (website.socials?.items || [])) || []);
  const [dragIndex, setDragIndex] = useState(-1);
  const [overIndex, setOverIndex] = useState(-1);
  const itemRefs = useRef([]);

  const socialOptions = [
    'Facebook', 'Twitter', 'LinkedIn', 'GitHub', 'Instagram', 'YouTube', 'Tumblr', 'Pinterest', 'Reddit', 'Telegram', 'Email', 'Phone'
  ];

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const addItem = () => {
    setItems(prev => [...prev, { text: '', url: '', social: '' }]);
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const moveItem = (from, to) => {
    setItems(prev => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const onDragStart = (idx, e) => {
    setDragIndex(idx);
    setOverIndex(idx);
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = itemRefs.current[idx];
    if (el && typeof e.dataTransfer.setDragImage === 'function') {
      e.dataTransfer.setDragImage(el, 16, 16);
    }
  };

  const onDragEnter = (idx) => {
    if (idx !== overIndex) setOverIndex(idx);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (idx, e) => {
    e.preventDefault();
    const from = dragIndex;
    const to = idx;
    if (from !== -1 && to !== -1 && from !== to) moveItem(from, to);
    setDragIndex(-1);
    setOverIndex(-1);
  };

  const onDragEnd = () => {
    setDragIndex(-1);
    setOverIndex(-1);
  };

  const isHttpUrl = (u) => /^(https?:)\/\//i.test((u || '').trim());
  const isMailto = (u) => /^mailto:/i.test((u || '').trim());
  const isTel = (u) => /^tel:/i.test((u || '').trim());

  const validateUrlForPlatform = (platform, url) => {
    const val = (url || '').trim();
    if (platform === 'Email') return isMailto(val) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    if (platform === 'Phone') return isTel(val) || /^\+?[0-9\s().-]{5,}$/.test(val);
    return isHttpUrl(val);
  };

  const save = async (e) => {
    e.preventDefault();
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.social || !socialOptions.includes(it.social)) {
        toast.error(`Row ${i + 1}: platform is required`);
        return;
      }
      if (!it.url || !validateUrlForPlatform(it.social, it.url)) {
        toast.error(`Row ${i + 1}: url is invalid for ${it.social}`);
        return;
      }
    }
    try {
      setSaving(true);
      const payload = { socials: items };
      const resp = await patch(`websites/${website.id}/socials`, payload);
      const updated = resp?.website || { ...website, socials: items };
      onUpdated?.(updated);
    } catch (e) {
      toast.error('Failed to save socials.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="structure-header-row">
        <h5 className="mb-0">Social</h5>
        <div className="structure-controls">
          <button type="button" className="structure-add-child-btn" onClick={addItem}>+ Add social</button>
        </div>
      </div>
      <p className="muted-hint mt-1">Add links to your social profiles and contact channels.</p>
      <Form onSubmit={save}>
        {items.length === 0 ? (
          <div className="structure-empty">
            <div className="structure-empty-icon">⊕</div>
            <div className="structure-empty-title">No social links yet</div>
            <div className="structure-empty-hint">Click "+ Add social" above to add your first link.</div>
          </div>
        ) : (
          items.map((it, idx) => (
            <div
              key={idx}
              className={[
                'structure-node',
                dragIndex === idx ? 'structure-node--dragging' : '',
                overIndex === idx && dragIndex !== -1 && dragIndex !== idx ? 'structure-node--drag-before' : '',
              ].filter(Boolean).join(' ')}
              ref={(el) => (itemRefs.current[idx] = el)}
            >
              <div
                className="structure-node-header"
                draggable
                onDragStart={(e) => onDragStart(idx, e)}
                onDragEnter={() => onDragEnter(idx)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(idx, e)}
                onDragEnd={onDragEnd}
              >
                <div className="structure-node-left">
                  <span className="structure-drag-handle" aria-hidden>
                    <FiMenu size={12} />
                  </span>
                  <select
                    className="social-platform-select"
                    value={it.social}
                    onChange={(e) => updateField(idx, 'social', e.target.value)}
                  >
                    <option value="">Platform</option>
                    {socialOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <input
                    type="text"
                    className="social-url-input"
                    value={it.url}
                    onChange={(e) => updateField(idx, 'url', e.target.value)}
                    placeholder="https://... or mailto: or tel:"
                  />
                </div>
                <div className="structure-node-actions">
                  <button
                    type="button"
                    className="structure-delete-btn"
                    title="Remove"
                    onClick={() => removeItem(idx)}
                  >×</button>
                </div>
              </div>
            </div>
          ))
        )}
        <div className="form-actions mt-3">
          <Button type="submit" className="primary-button" disabled={saving}>
            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default Social;
